import Cookies from 'js-cookie';
import React, { Suspense, lazy, useContext, useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';

// Components
import Loading from '../components/spinner/Loading.jsx';

// Context
import { ContentProcessContext } from '../context/ContentProcessContext.jsx';

// I18n
import i18n from '../i18n/es.json';

// Services
import { getAllProcessService } from '../services/Process';

// Constants
import { PROFILE_ID } from '../utils/constants';

// Routes
import PrivateRoute from './PrivateRoute.jsx';
import PublicRoute from './PublicRoute.jsx';

const Routes = () => {
  const isLogged = !!Cookies.get('auth');
  const auth = isLogged && JSON.parse(Cookies.get('auth'));
  const profile = auth.profileID;

  // Context
  const { setProcesses } = useContext(ContentProcessContext);

  // Views
  const LandingView = lazy(() => import('./LandingPage.jsx'));
  const AuthView = lazy(() => import('../views/authView/AuthView.jsx'));
  const HomeView = lazy(() => import('../views/homeView/HomeView.jsx'));
  const AddUserView = lazy(() => import('../views/addUserView/AddUserView.jsx'));
  const UpdateUserView = lazy(() => import('../views/updateUserView/UpdateUserView.jsx'));
  const AddRoleView = lazy(() => import('../views/addRoleView/AddRoleView.jsx'));
  const ListRoles = lazy(() => import('../views/listRoleView/ListRoleView.jsx'));
  const ListUserView = lazy(() => import('../views/listUserView/ListUserView.jsx'));
  const ListProcessView = lazy(() => import('../views/listProcessView/ListProcessView.jsx'));
  const AddGroup = lazy(() => import('../views/addGroupView/AddGroupView.jsx'));
  const UpdateGroup = lazy(() => import('../views/updateGroupView/UpdateGroupView.jsx'));
  const ListGroup = lazy(() => import('../views/listGroupView/ListGroup.jsx'));
  const UpdatePersonalSettingsView = lazy(() => import('../views/updatePersonalSettings/updatePersonalSettings.jsx'));
  const ResetPasswordView = lazy(() => import('../views/restorePasswordView/RestorePasswordView.jsx'));
  const ChangePasswordView = lazy(() => import('../views/changePassword/ChangePasswordView.jsx'));
  const AddSequenceView = lazy(() => import('../views/addSequenceView/AddSequenceView.jsx'));
  const AdminSequenceView = lazy(() => import('../views/adminSequenceView/AdminSequenceView.jsx'));
  const addEmailTemplate = lazy(() => import('../views/addEmailView/AddEmailView.jsx'));
  const AddDocumentTemplate = lazy(() => import('../views/addDocumentView/AddDocumentView.jsx'));
  const AdminTemplateView = lazy(() => import('../views/adminTemplateView/AdminTemplateView.jsx'));
  const AdminTemplateDocumentView = lazy(() =>
    import('../views/adminTemplateDocumentView/AdminTemplateDocumentView.jsx')
  );
  const ListDynamicForm = lazy(() => import('../views/listDynamicForm/ListDynamicForm.jsx'));
  const EditDynamicForm = lazy(() => import('../views/editDynamicForm/EditDynamicForm.jsx'));
  const CopilotFormView = lazy(() => import('../views/copilotFormView/CopilotFormView.jsx'));
  const AddProcessView = lazy(() => import('../views/addProcessView/AddProcessView.jsx'));
  const ContentProcessView = lazy(() => import('../views/contentProcessView/ContentProcessView.jsx'));
  const ImageProcessView = lazy(() => import('../views/imageProcessView/ImageProcessView.jsx'));
  const InformationSourceView = lazy(() => import('../views/informationSourceView/InformationSourceView.jsx'));
  const AddInformationSourceView = lazy(() => import('../views/addInformationSourceView/AddInformationSourceView.jsx'));
  const TaskAsignation = lazy(() => import('../views/taskAsignation/TaskAsignation.jsx'));
  const TaskEvents = lazy(() => import('../views/taskEvents/TaskEvents.jsx'));
  const ConfigTasksView = lazy(() => import('../views/configTasksView/ConfigTasksView.jsx'));
  const ConfigSlaView = lazy(() => import('../views/configSlaView/ConfigSlaView.jsx'));
  const ImageProcessTaskView = lazy(() => import('../views/imageProcessTaskView/ImageProcessTaskView.jsx'));
  const UpdateInformationSourceView = lazy(() =>
    import('../views/updateInformationSourceView/UpdateInformationSourceView.jsx')
  );
  const ProcessInstanceCreationView = lazy(() =>
    import('../views/processInstanceCreationView/ProcessInstanceCreationView.jsx')
  );
  const AdminExternalSceView = lazy(() => import('../views/adminExternalScesView/AdminExternalScesView.jsx'));
  const AddExternalSourceView = lazy(() => import('../views/addExternalSourceView/AddExternalSourceView.jsx'));
  const IndividualSourceMappingView = lazy(() =>
    import('../views/individualSourceMappingView/IndividualSourceMappingView.jsx')
  );
  const MultipleSourceMappingView = lazy(() =>
    import('../views/multipleSourceMappingView/MultipleSourceMappingView.jsx')
  );
  const AdminComponentsView = lazy(() => import('../views/adminComponentsView/AdminComponentsView.jsx'));
  const AddComponentsView = lazy(() => import('../views/addComponent/AddComponentView.jsx'));
  const TasksView = lazy(() => import('../views/tasksView/TasksView.jsx'));
  const TaskInstanceView = lazy(() => import('../views/taskInstanceView/TaskInstanceView.jsx'));
  const TaskInstanceEditView = lazy(() => import('../views/taskInstanceEditView/TaskInstanceEditView.jsx'));
  const AdminCalendarView = lazy(() => import('../views/adminCalendarView/AdminCalendarView.jsx'));
  const PermissionsView = lazy(() => import('../views/permissionsView/PermissionsView.jsx'));
  const UpdatePermissionsView = lazy(() => import('../views/updatePermissionsView/UpdatePermissionsView.jsx'));
  const ProcessSearchView = lazy(() => import('../views/processSearchView/ProcessSearchView.jsx'));
  const PendingTaskView = lazy(() => import('../views/report/PendingTaskView.jsx'));
  const ProcessHistoryView = lazy(() => import('../views/report/ProcessHistoryView.jsx'));
  const IAReportView = lazy(() => import('../views/iaReportView/IAReportView.jsx'));
  const ProcessMonthlyManagementView = lazy(() => import('../views/report/ProcessMonthlyManagementView.jsx'));
  const ProcessMonthlyManagementByAreaView = lazy(() =>
    import('../views/report/ProcessMonthlyManagementByAreaView.jsx')
  );
  const ProcessReportsMunicipalityView = lazy(() => import('../views/report/ProcessReportsMunicipalityView.jsx'));
  const ViewDocumentTemplate = lazy(() => import('../views/viewDocumentTemplate/ViewDocumentTemplate.jsx'));
  const ProcessInstanceVisualizerView = lazy(() =>
    import('../views/processInstanceVisualizerView/ProcessInstanceVisualizerView.jsx')
  );
  const ReassignTask = lazy(() => import('../views/reassignTask/ReassignTask.jsx'));
  const TaskReassignmentView = lazy(() => import('../views/taskReassignmentView/TaskReassignmentView.jsx'));
  const ListProcessInstancesView = lazy(() => import('../views/listProcessInstancesView/ListProcessInstancesView.jsx'));
  const ImportSchemaView = lazy(() => import('../views/importSchemaView/ImportSchemaView.jsx'));
  const CallbackConfigView = lazy(() => import('../views/callbackConfigView/CallbackConfigView.jsx'));
  const AnonymProjectCreated = lazy(() => import('../views/anonymProjectCreated/AnonymProjectCreated.jsx'));
  const MonitoringVariable = lazy(() => import('../views/monitoringVariable/MonitoringVariable.jsx'));
  const ListServiceTypes = lazy(() => import('../views/listServiceTypesView/ListServiceTypesView.jsx'));
  const AddServiceType = lazy(() => import('../views/addServiceTypeView/AddServiceTypeView.jsx'));
  const AddReportedSituationView = lazy(() => import('../views/addReportedSituationView/AddReportedSituationView.jsx'));
  const ListReportedSituationView = lazy(() =>
    import('../views/listReportedSituationView/ListReportedSituationView.jsx')
  );
  const ListCatalogView = lazy(() => import('../views/listCatalogView/ListCatalogView.jsx'));
  const AddCatalogView = lazy(() => import('../views/addCatalogView/AddCatalogView.jsx'));
  // ------------------------------------------- //
  // -------------- Action Handlers ------------ //
  // ------------------------------------------- //

  const handleGetAllProcess = async () => {
    const filtering = `processFilter=&stateId=0`;
    try {
      const result = await getAllProcessService({ filterData: filtering });
      setProcesses(result?.data ? result.data : null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ------------------------------------------- //
  // ---------- Side Effects Handlers ---------- //
  // ------------------------------------------- //

  useEffect(() => {
    if (profile === PROFILE_ID.STATE_OPERATOR) {
      handleGetAllProcess();
    }
  }, [profile]);

  // ------------------------------------------- //
  // ----------------- Return ------------------ //
  // ------------------------------------------- //

  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        {!isLogged && <PublicRoute path="/" exact component={LandingView} />}
        {!isLogged && <PublicRoute path="/auth" component={AuthView} />}
        {isLogged && <Route path="/" exact component={() => <Redirect to={{ pathname: '/home' }} />} />}
        {isLogged && <PrivateRoute pageName={i18n.viewTitles.home} path="/home" component={HomeView} />}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.user} path="/addUser" component={AddUserView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.user} path="/editUser" component={UpdateUserView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.user} path="/listUsers" component={ListUserView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.role} path="/addRole" component={AddRoleView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.role} path="/listRoles" component={ListRoles} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.group} path="/addGroup" component={AddGroup} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.group} path="/updateGroup" component={UpdateGroup} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.role} path="/editRole" component={AddRoleView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.group} path="/listGroup" component={ListGroup} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.citizenAttention}
            path="/adminServiceTypes"
            component={ListServiceTypes}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.citizenAttention} path="/addServiceType" component={AddServiceType} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.citizenAttention}
            path="/updateServiceType"
            component={AddServiceType}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.citizenAttention}
            path="/reportedSituation"
            component={ListReportedSituationView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.citizenAttention}
            path="/addReportedSituation"
            component={AddReportedSituationView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.citizenAttention}
            path="/updateReportedSituation"
            component={AddReportedSituationView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.citizenAttention} path="/catalogs" component={ListCatalogView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.citizenAttention} path="/addCatalog" component={AddCatalogView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.citizenAttention} path="/updateCatalog" component={AddCatalogView} />
        )}
        {isLogged && (
          <PrivateRoute
            pageName={i18n.viewTitles.userOptions}
            path="/editarInformacionPersonal"
            component={UpdatePersonalSettingsView}
          />
        )}
        {isLogged && (
          <PrivateRoute pageName={i18n.viewTitles.userOptions} path="/resetPassword" component={ResetPasswordView} />
        )}
        {isLogged && (
          <PrivateRoute pageName={i18n.viewTitles.userOptions} path="/changePassword" component={ChangePasswordView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.sequence} path="/addSequence" component={AddSequenceView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.sequence} path="/adminSequence" component={AdminSequenceView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.sequence} path="/updateSequence" component={AddSequenceView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.template} path="/addTemplate" component={addEmailTemplate} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.template} path="/documentTemplate" component={AddDocumentTemplate} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.template} path="/updateTemplate" component={addEmailTemplate} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.template} path="/adminTemplates" component={AdminTemplateView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.template}
            path="/adminDocumentTemplates"
            component={AdminTemplateDocumentView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/process" component={ListProcessView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/taskReassignment" component={TaskReassignmentView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/listDynamicForm" component={ListDynamicForm} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName="Copilot" path="/copilotForm" component={CopilotFormView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.processNameDymmy}
            path="/editDynamicForm"
            component={EditDynamicForm}
            hideHeader
            hideSidebar
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/createProcess" component={AddProcessView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/updateProcess" component={AddProcessView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/processContent" component={ContentProcessView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/imageProcess" component={ImageProcessView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/importSchema" component={ImportSchemaView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName="Callbacks" path="/callbackConfig" component={CallbackConfigView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.process}
            path="/informationSources"
            component={InformationSourceView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.process}
            path="/createInformationSource"
            component={AddInformationSourceView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.process}
            path="/updateInformationSource"
            component={UpdateInformationSourceView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/taskAsignation" component={TaskAsignation} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/taskEvents" component={TaskEvents} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/configTasks" component={ConfigTasksView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.process} path="/configSla" component={ConfigSlaView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.createProcessInstance}
            path="/creationForm"
            component={ProcessInstanceCreationView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.reportPendingTask}
            path="/reportPendingTask"
            component={PendingTaskView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.reportProcessHistory}
            path="/reportProcessHistory"
            component={ProcessHistoryView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute pageName={i18n.viewTitles.ReportIA} path="/ia-report" component={IAReportView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.reportMonthlyManagement}
            path="/reportMonthlyManagement"
            component={ProcessMonthlyManagementView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.reportMonthlyManagementByArea}
            path="/reportMonthlyManagementByArea"
            component={ProcessMonthlyManagementByAreaView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.reportReportsMunicipality}
            path="/reportReportsMunicipality"
            component={ProcessReportsMunicipalityView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute pageName={i18n.viewTitles.myTasks} path="/myTasks" component={TasksView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.myTasks}
            path="/processTaskInstance"
            component={ImageProcessTaskView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute pageName={i18n.viewTitles.taskForm} path="/taskForm" component={TaskInstanceView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute pageName={i18n.viewTitles.taskForm} path="/taskEditForm" component={TaskInstanceEditView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute pageName={i18n.viewTitles.searches} path="/processSearch" component={ProcessSearchView} />
        )}
        {isLogged && (profile === PROFILE_ID.STATE_OPERATOR || profile === PROFILE_ID.STATE_PROCESS_ADMIN) && (
          <PrivateRoute
            pageName={i18n.viewTitles.processVisualizer}
            path="/processInstanceVisualizer"
            component={ProcessInstanceVisualizerView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute pageName={i18n.viewTitles.documentView} path="/viewDocument" component={ViewDocumentTemplate} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute
            pageName={i18n.viewTitles.monitoring}
            path="/monitoringVariable"
            component={MonitoringVariable}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_OPERATOR && (
          <PrivateRoute pageName={i18n.viewTitles.reassignTask} path="/reassignTask" component={ReassignTask} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.externalSces}
            path="/externalSources"
            component={AdminExternalSceView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.calendar} path="/calendar" component={AdminCalendarView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.externalSces}
            path="/addOrEditExternalSource"
            component={AddExternalSourceView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.components} path="/components" component={AdminComponentsView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.components} path="/addComponent" component={AddComponentsView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.components}
            path="/individualMapping"
            component={IndividualSourceMappingView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.components}
            path="/multipleMappingRest"
            component={MultipleSourceMappingView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute pageName={i18n.viewTitles.permissions} path="/permissions" component={PermissionsView} />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.permissions}
            path="/editPermissions"
            component={UpdatePermissionsView}
          />
        )}
        {isLogged && profile === PROFILE_ID.STATE_PROCESS_ADMIN && (
          <PrivateRoute
            pageName={i18n.viewTitles.process}
            path="/listProcessInstances"
            component={ListProcessInstancesView}
          />
        )}
        {/* this routes are for anonym process creation */}
        <PrivateRoute
          pageName={i18n.viewTitles.anonymProcess}
          path="/createAnonymProcess/:processDescriptor"
          component={ProcessInstanceCreationView}
          hideSidebar
        />
        <PrivateRoute
          pageName={i18n.viewTitles.anonymProcess}
          path="/anonymProcessCreated"
          component={AnonymProjectCreated}
          hideHeader
          hideSidebar
        />
        {/* this routes are for anonym process creation */}
        {isLogged && <Route path="*" exact component={() => <Redirect to={{ pathname: '/home' }} />} />}
        {!isLogged && <Route path="*" exact component={() => <Redirect to={{ pathname: '/auth' }} />} />}
      </Switch>
    </Suspense>
  );
};

export default Routes;
